/* eslint-disable jsx-a11y/anchor-is-valid */

import React, { useCallback, useEffect, useState } from "react";

import Dropdown from "react-bootstrap/esm/Dropdown";
import { Link } from "react-router-dom";
import { MUIIcon } from "../../framework/widgets";
import { ProfilePicture } from "../../framework/user";
import clsx from "clsx";
import { useAuth } from "../../modules/auth";
import { useContactConnections } from "../../framework/sites/site-entity/useContactConnections";

const useProfilePageUrl = () => {
    const { user, site, selectedEntityId } = useAuth()
    const [pageUrl, setPageUrl] = useState<string>()
    const { connections } = useContactConnections()

    useEffect(() => {
        if (!site?.profilePageId) {
            setPageUrl("");
            return;
        }

        if (connections?.length === 0 && user?.entityId) {
            setPageUrl(
                `/site(${site.portalId})/page(${site.profilePageId})?id=${user.entityId}`
            );
            return;
        }

        if (connections?.length === 1) {
            const connectionEntityId = connections[0]?.contactId;

            if (connectionEntityId) {
                setPageUrl(
                    `/site(${site.portalId})/page(${site.profilePageId})?id=${connectionEntityId}`
                );
            } else {
                setPageUrl("");
            }
            return;
        }

        if (connections?.length > 1) {
            if (selectedEntityId) {
                setPageUrl(
                    `/site(${site.portalId})/page(${site.profilePageId})?id=${selectedEntityId}`
                );
            } else {
                setPageUrl("");
            }
            return;
        }

        setPageUrl("");
    }, [site, user?.entityId, connections, selectedEntityId]);

    return { pageUrl };
}

export const HeaderUserMenu = ({ className }: { className?: string }) => {
    const itemClass = "text-gray-800 text-hover-primary fw-semibold px-5 py-2"
    const iconClass = "text-gray-800 text-hover-primary me-1"
    const { user } = useAuth()
    const { connections, selectEntityId } = useContactConnections()
    const { pageUrl } = useProfilePageUrl()

    const hasImpersonateRole = useCallback(() => {
        if (!user) return false;
        return user.roles.findIndex(p => p.toUpperCase() === 'IMPERSONATE') !== -1;
    }, [user])

    return (
        <Dropdown>
            <Dropdown.Toggle as={UserToggle} id="dropdown-autoclose-inside">
                <div className={clsx('cursor-pointer symbol', className)}>
                    <ProfilePicture />
                </div>
            </Dropdown.Toggle>

            <Dropdown.Menu as={UserMenu}>
                {(pageUrl) && <>
                    <Dropdown.Item eventKey="1" as={Link} to={pageUrl} className={itemClass}>
                        <MUIIcon iconName="FolderSharedOutlined" className={iconClass} /> User Profile
                    </Dropdown.Item>
                </>}
                <Dropdown.Item eventKey="2" as={Link} to={'/sites'} className={itemClass}>
                    <MUIIcon iconName="DashboardCustomizeOutlined" className={iconClass} /> Sites
                </Dropdown.Item>
                {(hasImpersonateRole()) && <>
                    <Dropdown.Item eventKey="3" as={Link} to={'/impersonate'} className={itemClass}>
                        <MUIIcon iconName="SupervisedUserCircleOutlined" className={iconClass} /> Impersonate
                    </Dropdown.Item>
                </>}
                <Dropdown.Item eventKey="4" as={Link} to={'/change-password'} className={itemClass}>
                    <MUIIcon iconName="PasswordOutlined" className={iconClass} /> Change Password
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item eventKey="5" as={Link} to={'/logout'} >
                    <MUIIcon iconName="ExitToAppOutlined" className={iconClass} /> Sign Out
                </Dropdown.Item>
                <>
                    <Dropdown.Divider />
                    {(connections || [])?.length > 1 && <>
                        <Dropdown.Item eventKey="6" onClick={() => selectEntityId()}>
                            <MUIIcon iconName="PeopleOutlined" className={iconClass} /> Switch Profile
                        </Dropdown.Item>
                    </>}
                </>
            </Dropdown.Menu>
        </Dropdown>
    )
}

const UserToggle = React.forwardRef<HTMLAnchorElement, {
    children?: React.ReactNode;
    onClick: (e: React.MouseEvent) => void;
}>(({ children, onClick }, ref) => (
    <a
        href=""
        ref={ref}
        onClick={(e) => {
            e.preventDefault();
            onClick(e);
        }}
    >
        {children}
    </a>
));

const UserMenu = React.forwardRef<HTMLDivElement, {
    style?: React.CSSProperties,
    className?: string,
    children?: React.ReactNode
    "aria-labelledby": string,
    onClick: (e: React.MouseEvent) => void
}>(
    ({ children, style, className, 'aria-labelledby': labeledBy }, ref) => {
        const { user } = useAuth()

        return (
            <div
                ref={ref}
                style={style}
                className={clsx(className, "fw-bolder fs-6")}
                aria-labelledby={labeledBy}
            >
                <div className='d-flex align-items-center py-3 px-5'>
                    <div className='symbol symbol-50px me-5'>
                        <ProfilePicture />
                    </div>

                    {user && <div className='d-flex flex-column'>
                        <div className='fw-bolder d-flex align-items-center fs-5 text-uppercase'>
                            {user.userName}
                        </div>
                        <a href='#' className='fw-bold text-muted text-hover-primary fs-7 text-lowercase'>
                            {user.email}
                        </a>
                    </div>}
                </div>
                <div className="mt-2">
                    {children}
                </div>
            </div>
        );
    },
)
